import type { ConfigTenantId, GeneratedConfigTenantId } from 'types';
import type {
  ResolvedModule,
  ModuleConfig,
  DomainEntityConfiguration,
} from '@web-config-app/core';
import { mapStringToCategoryType } from '@web-config-app/core';
import GeneratedTenantConfigs from './json/tenants.json';
import GeneratedModuleConfigs from './json/modules.json';
import GeneratedEntityConfigs from './json/entities.json';

type TenantModuleReference = {
  id: string;
  modules?: TenantModuleReference[];
  isUsingConfigAppBackend?: boolean;
  params?: Record<string, unknown>;
};

type ModuleConfigEntry = ModuleConfig & Record<string, unknown>;

const MAX_MODULE_DEPTH = 3;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Recursively merges two plain objects. Values from `override` win unless
 * both sides are plain objects, in which case they are merged key-by-key.
 * Arrays and primitives from `override` replace the base value outright.
 */
function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, overrideValue]) => {
    const baseValue = result[key];
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      result[key] = deepMerge(baseValue, overrideValue);
    } else {
      result[key] = overrideValue;
    }
  });
  return result;
}

/**
 * Deep merges param defaults from the component page's JSON Schema
 * with tenant-provided param override values. Schema defaults are
 * used when the tenant doesn't supply a value. For object-valued
 * params, tenant overrides are merged into defaults per-key rather
 * than replacing the entire default object.
 */
function resolveParams(
  paramsSchema: Record<string, unknown> | undefined,
  tenantParams?: Record<string, unknown>,
): Record<string, unknown> {
  if (!paramsSchema) return tenantParams ?? {};

  const defaults: Record<string, unknown> = {};
  const properties =
    (paramsSchema as { properties?: Record<string, { default?: unknown }> })
      .properties ?? {};

  Object.entries(properties).forEach(([key, schema]) => {
    if (schema.default !== undefined) {
      defaults[key] = schema.default;
    }
  });

  return tenantParams ? deepMerge(defaults, tenantParams) : defaults;
}

/**
 * Resolves a module's children, enforcing MAX_MODULE_DEPTH. Depth is
 * also validated at config-generation time (see `generate-configs` —
 * both container registry nesting and tenant module overrides throw if
 * they exceed three levels). Reaching the limit here therefore means
 * the generated tree disagrees with the generator's invariants, so we
 * throw to surface the bug instead of silently dropping modules.
 */
function resolveChildren(
  parentId: string,
  childRefs: TenantModuleReference[],
  depth: number,
): ResolvedModule[] {
  if (depth >= MAX_MODULE_DEPTH) {
    if (childRefs.length > 0) {
      throw new Error(
        `Module "${parentId}" has children at depth ${depth} which exceeds MAX_MODULE_DEPTH (${MAX_MODULE_DEPTH}). This should have been rejected by \`pnpm generate-configs\` — please re-run it.`,
      );
    }
    return [];
  }
  return childRefs
    .map((childRef) =>
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      resolveModule(childRef, depth + 1),
    )
    .filter((child): child is ResolvedModule => child !== null);
}

/**
 * Resolves a module reference into a ResolvedModule by looking up
 * the ID in modules.json first, then falling back to entities.json.
 * Recursively resolves child modules for containers and entities.
 */
function resolveModule(
  moduleRef: TenantModuleReference,
  depth: number,
): ResolvedModule | null {
  const moduleConfig = GeneratedModuleConfigs[
    moduleRef.id as keyof typeof GeneratedModuleConfigs
  ] as ModuleConfigEntry | undefined;

  // Not in modules.json — resolve as entity from entities.json
  if (!moduleConfig) {
    const entityConfig = GeneratedEntityConfigs[
      moduleRef.id as keyof typeof GeneratedEntityConfigs
    ] as DomainEntityConfiguration | undefined;

    if (!entityConfig) {
      throw new Error(
        `Unknown module ID "${moduleRef.id}" — not found in modules.json or entities.json`,
      );
    }

    const category = mapStringToCategoryType(entityConfig.category);

    if (category === 'hidden') {
      return null;
    }

    const childRefs: TenantModuleReference[] = moduleRef.modules ?? [];
    const resolvedChildren = resolveChildren(moduleRef.id, childRefs, depth);

    return {
      id: entityConfig.id,
      labelKey: entityConfig.labelKey,
      type: 'entity' as const,
      ...(entityConfig.icon && { icon: entityConfig.icon }),
      ...(resolvedChildren.length > 0 && { modules: resolvedChildren }),
      ...(typeof moduleRef.isUsingConfigAppBackend === 'boolean' && {
        isUsingConfigAppBackend: moduleRef.isUsingConfigAppBackend,
      }),
    };
  }

  const baseModule = {
    id: moduleConfig.id,
    labelKey: moduleConfig.labelKey,
    icon: moduleConfig.icon,
    ...(moduleConfig.permissions && { permissions: moduleConfig.permissions }),
  };

  switch (moduleConfig.type) {
    case 'container': {
      // Use tenant override children if provided, otherwise use container defaults
      const childRefs: TenantModuleReference[] =
        moduleRef.modules ?? moduleConfig.modules ?? [];

      const resolvedChildren = resolveChildren(moduleRef.id, childRefs, depth);

      // Auto-hide: container with no visible children and no page → skip
      if (resolvedChildren.length === 0) {
        return null;
      }

      return {
        ...baseModule,
        type: 'container' as const,
        modules: resolvedChildren,
      };
    }

    case 'schemaPage':
      return {
        ...baseModule,
        type: 'schemaPage' as const,
        schema: moduleConfig.schema,
        ...(moduleConfig.environmentSelection !== undefined && {
          environmentSelection: moduleConfig.environmentSelection,
        }),
        operation: moduleConfig.operation,
      };

    case 'componentPage': {
      const resolvedParams = resolveParams(
        moduleConfig.params as Record<string, unknown> | undefined,
        moduleRef.params,
      );

      // Component pages may have child modules (e.g. a landing page with
      // sub-navigation). Use tenant override children if provided, otherwise
      // fall back to the component page's default children.
      const childRefs: TenantModuleReference[] =
        moduleRef.modules ?? moduleConfig.modules ?? [];
      const resolvedChildren = resolveChildren(moduleRef.id, childRefs, depth);

      return {
        ...baseModule,
        type: 'componentPage' as const,
        ...(Object.keys(resolvedParams).length > 0 && {
          params: resolvedParams,
        }),
        ...(resolvedChildren.length > 0 && { modules: resolvedChildren }),
      };
    }

    default:
      throw new Error(`Unknown module type for "${moduleRef.id}"`);
  }
}

/**
 * Resolves the full module tree for a tenant. Reads the tenant's
 * module references from tenants.json and recursively resolves
 * each one from modules.json and entities.json.
 *
 * @returns Array of top-level ResolvedModule (typically tab-level containers)
 */
export function getGeneratedModuleTreeForTenant(
  tenantId: ConfigTenantId<GeneratedConfigTenantId>,
): ResolvedModule[] {
  const tenantData = GeneratedTenantConfigs[tenantId];
  const tenantModules: TenantModuleReference[] = tenantData.modules ?? [];

  return tenantModules
    .map((moduleRef) => resolveModule(moduleRef, 1))
    .filter((module): module is ResolvedModule => module !== null);
}
